<template>
    <section v-if="revision" class="revision-proposal">
        <header class="revision-header">
            <strong>{{ $t('AI 평가 수정안') }}</strong>
            <b-badge :variant="statusMeta.variant">{{ $t(statusMeta.label) }}</b-badge>
        </header>

        <div v-if="revision.explanation" class="revision-section">
            <h6>{{ $t('제안 사유') }}</h6>
            <p>{{ revision.explanation }}</p>
        </div>

        <b-alert :show="isStale" variant="danger" class="small py-2 px-3">
            {{ $t('현재 평가 결과가 수정안 생성 시점과 달라 적용할 수 없습니다. 최신 상태를 확인해 주세요.') }}
        </b-alert>

        <div v-if="affectedItems.length" class="revision-section">
            <h6>{{ $t('적용 시 변경되는 전체 평가 항목') }}</h6>
            <article v-for="(item, itemIndex) in affectedItems"
                     :key="`affected-${item.evId || itemIndex}`"
                     class="affected-item"
            >
                <div class="affected-title">
                    {{ $t('평가 항목') }} {{ item.evId || $t('식별자 없음') }}
                    <b-badge v-if="isRelatedItem(item, itemIndex)" variant="info">{{ $t('함께 변경되는 항목') }}</b-badge>
                </div>
                <div v-if="diffsFor(item).length" class="diff-list">
                    <div v-for="diff in diffsFor(item)" :key="diff.path" class="diff-row">
                        <code>{{ diff.path }}</code>
                        <div class="diff-values">
                            <div>
                                <span>{{ $t('변경 전') }}</span>
                                <pre>{{ formatValue(diff.before) }}</pre>
                            </div>
                            <div>
                                <span>{{ $t('변경 후') }}</span>
                                <pre>{{ formatValue(diff.after) }}</pre>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="text-muted small">{{ $t('표시할 실제 변경 필드가 없습니다.') }}</div>
            </article>
        </div>
        <div v-else class="revision-section text-muted small">
            {{ $t('변경 예상 항목을 확인할 수 없습니다.') }}
        </div>

        <dl v-if="hasPreviewMetadata" class="preview-metadata">
            <template v-if="hasConfidence">
                <dt>{{ $t('적용 후 예상 신뢰도') }}</dt>
                <dd>{{ confidenceText }}</dd>
            </template>
            <template v-if="revision.preview.action">
                <dt>{{ $t('적용 후 예상 조치') }}</dt>
                <dd>{{ actionText }}</dd>
            </template>
        </dl>

        <footer v-if="revision.status === 'pending'" class="revision-actions">
            <b-button size="sm" variant="outline-danger" :disabled="!canReject" @click="$emit('reject', revision)">
                {{ $t('거절') }}
            </b-button>
            <b-button size="sm" variant="primary" :disabled="!canApply" @click="$emit('apply', revision)">
                {{ $t('적용') }}
            </b-button>
        </footer>
    </section>
</template>

<script>
import presentation from './aiQnaPresentation';

const {
    canApplyRevision,
    canRejectRevision,
    flattenLeafDiffs,
    revisionStatusMeta,
} = presentation;

export default {
    name: 'AIQnaRevisionProposal',
    props: {
        revision: {
            type: Object,
            default: null,
        },
        busy: {
            type: Boolean,
            default: false,
        },
        sending: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        statusMeta() {
            return revisionStatusMeta(this.revision?.status);
        },
        isBusy() {
            return this.busy || this.sending;
        },
        isStale() {
            return this.revision?.preview?.stale === true;
        },
        affectedItems() {
            return Array.isArray(this.revision?.preview?.affectedItems)
                ? this.revision.preview.affectedItems
                : [];
        },
        canApply() {
            return canApplyRevision(this.revision, this.isBusy);
        },
        canReject() {
            return canRejectRevision(this.revision, this.isBusy);
        },
        hasConfidence() {
            return typeof this.revision?.preview?.confidence === 'number';
        },
        confidenceText() {
            return this.hasConfidence ? `${Math.round(this.revision.preview.confidence * 100)}%` : '';
        },
        hasPreviewMetadata() {
            return this.hasConfidence || Boolean(this.revision?.preview?.action);
        },
        actionText() {
            const action = this.revision?.preview?.action;
            if (action === 'manual_review') return this.$t('수동 검토');
            return action ? this.$t(action) : '';
        },
    },
    methods: {
        flattenLeafDiffs,
        diffsFor(item) {
            return flattenLeafDiffs(item.before, item.after);
        },
        isRelatedItem(item, itemIndex) {
            if (item.evId != null && this.revision?.evId != null) {
                return String(item.evId) !== String(this.revision.evId);
            }
            return itemIndex > 0;
        },
        formatValue(value) {
            if (value === undefined) return this.$t('없음');
            if (value === null) return this.$t('값 없음');
            if (typeof value === 'string') return value;
            if (typeof value === 'object') return JSON.stringify(value, null, 2);
            return String(value);
        },
    },
};
</script>

<style scoped lang="scss">
.revision-proposal {
    margin-top: 8px;
    padding: 12px;
    border: 1px solid #ced4da;
    border-radius: 8px;
    background: #fff;
    color: #343a40;
}

.revision-header,
.revision-actions,
.affected-title {
    display: flex;
    align-items: center;
    gap: 8px;
}

.revision-header {
    justify-content: space-between;
}

.revision-section {
    margin-top: 10px;

    h6 {
        margin-bottom: 4px;
        font-size: 0.78rem;
        font-weight: 700;
    }

    p {
        margin: 0;
        white-space: pre-wrap;
    }
}

.affected-item {
    margin-top: 8px;
    padding: 8px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
}

.affected-title {
    margin-bottom: 6px;
    font-size: 0.78rem;
    font-weight: 700;
}

.diff-row + .diff-row {
    margin-top: 8px;
}

.diff-row > code {
    color: #495057;
    font-size: 0.72rem;
    word-break: break-all;
}

.diff-values {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
    margin-top: 3px;

    span {
        color: #6c757d;
        font-size: 0.7rem;
    }

    pre {
        margin: 2px 0 0;
        padding: 6px;
        border-radius: 4px;
        background: #f8f9fa;
        font-family: inherit;
        font-size: 0.75rem;
        white-space: pre-wrap;
        word-break: break-word;
    }
}

.preview-metadata {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 3px 10px;
    margin: 10px 0 0;
    font-size: 0.75rem;

    dt,
    dd {
        margin: 0;
    }
}

.revision-actions {
    justify-content: flex-end;
    margin-top: 12px;
}
</style>
